<template>
  <div class="transfer-history">
    <div class="header-section">
      <h1 class="page-title">
        <i class="fas fa-history"></i>
        Transaction History
      </h1>

      <!-- Date Filter -->
      <div class="date-filters">
        <div class="date-input-group">
          <label>From:</label>
          <input
            type="date"
            v-model="filters.startDate"
            @change="loadData"
            class="date-input"
          >
        </div>
        <div class="date-input-group">
          <label>To:</label>
          <input
            type="date"
            v-model="filters.endDate"
            @change="loadData"
            class="date-input"
          >
        </div>
        <button @click="resetFilters" class="reset-btn">
          <i class="fas fa-undo"></i>
          Reset
        </button>
      </div>
    </div>

    <!-- Tabs Navigation -->
    <div class="tabs-navigation">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        @click="switchTab(tab.key)"
        :class="['tab-btn', { 'active': currentTab === tab.key }]"
      >
        <i :class="tab.icon"></i>
        <span>{{ tab.label }}</span>
        <span class="tab-count" v-if="getTabCount(tab.key) > 0">
          {{ getTabCount(tab.key) }}
        </span>
      </button>
    </div>

    <!-- Transition wrapper -->
    <transition name="fade" mode="out-in">
      <!-- Loading State -->
      <div v-if="isLoading" key="loading" class="loading-container">
        <div class="loading-spinner">
          <i class="fas fa-spinner fa-spin"></i>
        </div>
        <p>Loading transactions...</p>
      </div>

      <!-- Content Area -->
      <div v-else :key="currentTab" class="content-area">
        <!-- All Transactions -->
        <div v-show="currentTab === 'all'" class="tab-content">
          <div v-if="currentTransactions.length > 0" class="transfers-list">
            <div
              v-for="transaction in currentTransactions"
              :key="transaction.id"
              class="transfer-item"
              :class="getTransactionTypeClass(transaction.transaction_type)"
            >
              <div class="transfer-avatar" :class="getAvatarClass(transaction.transaction_type)">
                <i :class="getTransactionIcon(transaction.transaction_type)"></i>
              </div>
              <div class="transfer-details">
                <div class="transfer-header">
                  <span class="transaction-label">{{ transaction.description }}</span>
                  <span class="amount" :class="{ 'positive': isPositiveTransaction(transaction.transaction_type), 'negative': !isPositiveTransaction(transaction.transaction_type) }">
                    {{ isPositiveTransaction(transaction.transaction_type) ? '+' : '-' }}{{ formatAmount(transaction.amount) }}
                  </span>
                </div>
                <div class="transfer-meta">
                  <span class="date">{{ formatTransferDate(transaction.created_at) }}</span>
                  <span class="reference">{{ transaction.payment_ref }}</span>
                </div>
                <div class="transfer-status">
                  <span :class="['status-badge', getStatusClass(transaction.status)]">
                    {{ transaction.status }}
                  </span>
                  <span v-if="transaction.related_user" class="related-user">
                    <i class="fas fa-user"></i> {{ transaction.related_user }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <i class="fas fa-inbox"></i>
            <h3>No Transactions</h3>
            <p>You haven't made any transactions in the selected period.</p>
          </div>
        </div>

        <!-- Wallet Operations (Deposits & Withdrawals) -->
        <div v-show="currentTab === 'wallet'" class="tab-content">
          <div v-if="currentTransactions.length > 0" class="transfers-list">
            <div
              v-for="transaction in currentTransactions"
              :key="transaction.id"
              class="transfer-item"
              :class="transaction.transaction_type === 'DEPOSIT' ? 'deposit-transaction' : 'withdrawal-transaction'"
            >
              <div class="transfer-avatar" :class="transaction.transaction_type === 'DEPOSIT' ? 'deposit' : 'withdrawal'">
                <i :class="transaction.transaction_type === 'DEPOSIT' ? 'fas fa-arrow-down' : 'fas fa-arrow-up'"></i>
              </div>
              <div class="transfer-details">
                <div class="transfer-header">
                  <span class="transaction-label">{{ transaction.description }}</span>
                  <span class="amount" :class="transaction.transaction_type === 'DEPOSIT' ? 'positive' : 'negative'">
                    {{ transaction.transaction_type === 'DEPOSIT' ? '+' : '-' }}{{ formatAmount(transaction.amount) }}
                  </span>
                </div>
                <div class="transfer-meta">
                  <span class="date">{{ formatTransferDate(transaction.created_at) }}</span>
                  <span class="reference">{{ transaction.payment_ref }}</span>
                </div>
                <div class="transfer-status">
                  <span :class="['status-badge', getStatusClass(transaction.status)]">
                    {{ transaction.status }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <i class="fas fa-wallet"></i>
            <h3>No Wallet Operations</h3>
            <p>You haven't made any deposits or withdrawals in the selected period.</p>
          </div>
        </div>

        <!-- Sent Transfers -->
        <div v-show="currentTab === 'sent'" class="tab-content">
          <div v-if="currentTransactions.length > 0" class="transfers-list">
            <div
              v-for="transaction in currentTransactions"
              :key="transaction.id"
              class="transfer-item sent-transfer"
            >
              <div class="transfer-avatar">
                <i class="fas fa-paper-plane"></i>
              </div>
              <div class="transfer-details">
                <div class="transfer-header">
                  <span class="recipient">To: {{ transaction.related_user || transaction.recipient_username }}</span>
                  <span class="amount negative">-{{ formatAmount(transaction.amount) }}</span>
                </div>
                <div class="transfer-meta">
                  <span class="date">{{ formatTransferDate(transaction.created_at) }}</span>
                  <span class="reference">{{ transaction.payment_ref }}</span>
                </div>
                <div class="transfer-status">
                  <span :class="['status-badge', getStatusClass(transaction.status)]">
                    {{ transaction.status }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <i class="fas fa-paper-plane"></i>
            <h3>No Sent Transfers</h3>
            <p>You haven't sent any money in the selected period.</p>
          </div>
        </div>

        <!-- Received Transfers -->
        <div v-show="currentTab === 'received'" class="tab-content">
          <div v-if="currentTransactions.length > 0" class="transfers-list">
            <div
              v-for="transaction in currentTransactions"
              :key="transaction.id"
              class="transfer-item received-transfer"
            >
              <div class="transfer-avatar received">
                <i class="fas fa-hand-holding-usd"></i>
              </div>
              <div class="transfer-details">
                <div class="transfer-header">
                  <span class="sender">From: {{ transaction.related_user || transaction.sender_username }}</span>
                  <span class="amount positive">+{{ formatAmount(transaction.amount) }}</span>
                </div>
                <div class="transfer-meta">
                  <span class="date">{{ formatTransferDate(transaction.created_at) }}</span>
                  <span class="reference">{{ transaction.payment_ref }}</span>
                </div>
                <div class="transfer-status">
                  <span :class="['status-badge', getStatusClass(transaction.status)]">
                    {{ transaction.status }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <i class="fas fa-inbox"></i>
            <h3>No Received Transfers</h3>
            <p>You haven't received any money in the selected period.</p>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'TransferHistory',
  data() {
    return {
      currentTab: 'all',
      isLoading: false,

      // Unified data storage
      allTransactions: [],

      // Filters
      filters: {
        startDate: null,
        endDate: null
      },

      // Tab configuration
      tabs: [
        { key: 'all', label: 'All', icon: 'fas fa-list' },
        { key: 'wallet', label: 'Wallet Ops', icon: 'fas fa-wallet' },
        { key: 'sent', label: 'Sent', icon: 'fas fa-paper-plane' },
        { key: 'received', label: 'Received', icon: 'fas fa-inbox' }
      ]
    };
  },

  computed: {
    currentTransactions() {
      let filtered = this.allTransactions;

      // Filter by tab
      if (this.currentTab === 'wallet') {
        filtered = filtered.filter(t =>
          t.transaction_type === 'DEPOSIT' || t.transaction_type === 'WITHDRAWAL'
        );
      } else if (this.currentTab === 'sent') {
        filtered = filtered.filter(t => t.transaction_type === 'TRANSFER_OUT');
      } else if (this.currentTab === 'received') {
        filtered = filtered.filter(t => t.transaction_type === 'TRANSFER_IN');
      }

      // Filter by date
      return this.filterTransactionsByDate(filtered);
    }
  },

  async mounted() {
    this.initializeDateFilters();
    await this.loadData();
  },

  methods: {
    async switchTab(tabKey) {
      this.currentTab = tabKey;
    },

    initializeDateFilters() {
      const today = new Date();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(today.getDate() - 30);

      this.filters.endDate = today.toISOString().split('T')[0];
      this.filters.startDate = thirtyDaysAgo.toISOString().split('T')[0];
    },

    async resetFilters() {
      this.initializeDateFilters();
      await this.loadData();
    },

    async loadData() {
      if (this.isLoading) return;

      this.isLoading = true;

      try {
        // Load unified wallet history from backend
        const response = await axios.get('/api/v1/savings/wallet/history/');

        console.log('📊 Loaded transactions:', response.data);

        // Process and sort all transactions
        this.allTransactions = this.processTransactionData(response.data);

      } catch (error) {
        console.error('❌ Error loading transaction data:', error);
        this.allTransactions = [];
      } finally {
        this.isLoading = false;
      }
    },
processTransactionData(data) {
  if (!Array.isArray(data)) return [];

  // Utiliser un Map pour garantir l'unicité par payment_ref normalisée
  const uniqueTransactions = new Map();

  // Compteur pour le suivi
  let duplicatesFound = 0;

  data.forEach(txn => {
    // Vérifier que la transaction est valide
    if (!txn || !txn.id || !txn.payment_ref) {
      console.warn('⚠️ Transaction invalide détectée:', txn);
      return;
    }

    // Normaliser la payment_ref en retirant les suffixes _OUT, _IN, etc.
    const normalizedRef = txn.payment_ref.replace(/_OUT$|_IN$/, '');

    // Si cette payment_ref normalisée existe déjà
    if (uniqueTransactions.has(normalizedRef)) {
      duplicatesFound++;
      console.warn('🔄 Doublon détecté et ignoré:', {
        original_ref: txn.payment_ref,
        normalized_ref: normalizedRef,
        amount: txn.amount,
        type: txn.transaction_type,
        date: txn.created_at
      });
      return;
    }

    // Ajouter la transaction unique avec la ref normalisée comme clé
    uniqueTransactions.set(normalizedRef, txn);
  });

  // Log du résultat
  console.log(`✅ Déduplication terminée: ${uniqueTransactions.size} transactions uniques (${duplicatesFound} doublons supprimés)`);

  // Convertir le Map en Array et trier par date décroissante
  return Array.from(uniqueTransactions.values())
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
},

// Nouvelle méthode de validation
isValidTransaction(transaction) {
  const requiredFields = ['id', 'amount', 'transaction_type', 'status', 'created_at'];

  for (const field of requiredFields) {
    if (transaction[field] === undefined || transaction[field] === null) {
      return false;
    }
  }

  // Validation du type de transaction
  const validTypes = ['DEPOSIT', 'WITHDRAWAL', 'TRANSFER_IN', 'TRANSFER_OUT'];
  if (!validTypes.includes(transaction.transaction_type)) {
    console.warn('Type de transaction invalide:', transaction.transaction_type);
    return false;
  }

  // Validation du montant
  if (isNaN(parseFloat(transaction.amount)) || parseFloat(transaction.amount) < 0) {
    console.warn('Montant invalide:', transaction.amount);
    return false;
  }

  return true;
},

    filterTransactionsByDate(transactions) {
      if (!this.filters.startDate && !this.filters.endDate) {
        return transactions;
      }

      return transactions.filter(transaction => {
        const transactionDate = new Date(transaction.created_at);
        return this.isDateInRange(transactionDate);
      });
    },

    isDateInRange(date) {
      const start = this.filters.startDate ? new Date(this.filters.startDate) : null;
      const end = this.filters.endDate ? new Date(this.filters.endDate) : null;

      if (start && end) {
        end.setHours(23, 59, 59, 999);
        return date >= start && date <= end;
      }

      if (start) return date >= start;
      if (end) {
        end.setHours(23, 59, 59, 999);
        return date <= end;
      }

      return true;
    },

    getTabCount(tabKey) {
      if (tabKey === 'all') return this.allTransactions.length;

      return this.allTransactions.filter(t => {
        if (tabKey === 'wallet') {
          return t.transaction_type === 'DEPOSIT' || t.transaction_type === 'WITHDRAWAL';
        } else if (tabKey === 'sent') {
          return t.transaction_type === 'TRANSFER_OUT';
        } else if (tabKey === 'received') {
          return t.transaction_type === 'TRANSFER_IN';
        }
        return false;
      }).length;
    },

    getTransactionIcon(type) {
      const icons = {
        'DEPOSIT': 'fas fa-arrow-down',
        'WITHDRAWAL': 'fas fa-arrow-up',
        'TRANSFER_OUT': 'fas fa-paper-plane',
        'TRANSFER_IN': 'fas fa-hand-holding-usd'
      };
      return icons[type] || 'fas fa-exchange-alt';
    },

    getTransactionTypeClass(type) {
      if (!type) return '';
      return type.toLowerCase().replace(/_/g, '-') + '-transaction';
    },

    getAvatarClass(type) {
      if (type === 'DEPOSIT') return 'deposit';
      if (type === 'WITHDRAWAL') return 'withdrawal';
      if (type === 'TRANSFER_IN') return 'received';
      return '';
    },

    isPositiveTransaction(type) {
      return type === 'DEPOSIT' || type === 'TRANSFER_IN';
    },

    formatAmount(amount) {
      if (!amount) return '0 FCFA';
      return `${parseFloat(amount).toLocaleString()} FCFA`;
    },

    formatTransferDate(dateString) {
      if (!dateString) return 'Unknown date';

      try {
        const date = new Date(dateString);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
        const transferDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        if (transferDate.getTime() === today.getTime()) {
          return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
        } else if (transferDate.getTime() === yesterday.getTime()) {
          return `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
        } else {
          return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        }
      } catch (error) {
        console.error('Date formatting error:', error);
        return dateString;
      }
    },

    getStatusClass(status) {
      if (!status) return 'status-unknown';

      const statusLower = status.toLowerCase();
      if (statusLower === 'completed' || statusLower === 'success') return 'status-success';
      if (statusLower === 'pending') return 'status-pending';
      if (statusLower === 'failed' || statusLower === 'error') return 'status-error';
      return 'status-unknown';
    }
  }
};
</script>

<style scoped>
/* [Gardez TOUT le CSS existant de votre TransferHistory.vue] */

/* Ajoutez ces styles supplémentaires pour les nouveaux types de transactions */
.deposit-transaction {
  border-left-color: #10b981;
}

.withdrawal-transaction {
  border-left-color: #ef4444;
}

.transfer-out-transaction {
  border-left-color: #3b82f6;
}

.transfer-in-transaction {
  border-left-color: #10b981;
}

.transfer-avatar.deposit {
  background: linear-gradient(135deg, #10b981, #059669);
}

.transfer-avatar.withdrawal {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}

.related-user {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: #64748b;
  background: #f1f5f9;
  padding: 4px 10px;
  border-radius: 12px;
  margin-left: 8px;
}

/* Réutilisez tout le CSS de votre TransferHistory.vue */
.transfer-history {
  max-width: 900px;
  margin: 0 auto;
  padding: 24px;
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  min-height: 100vh;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.header-section {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.page-title {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 0 0 24px 0;
  font-size: 2rem;
  font-weight: 700;
  color: #2d3748;
}

.page-title i {
  color: #3b82f6;
  font-size: 1.8rem;
}

.date-filters {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.date-input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.date-input-group label {
  font-weight: 600;
  color: #4a5568;
  font-size: 0.9rem;
}

.date-input {
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: white;
}

.date-input:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.reset-btn {
  background: linear-gradient(135deg, #1e40af, #3b82f6);
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.2s ease;
  margin-top: 20px;
}

.reset-btn:hover {
  transform: translateY(-2px);
}

.tabs-navigation {
  display: flex;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 8px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.tab-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px 24px;
  background: none;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.tab-btn:hover {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.tab-btn.active {
  background: linear-gradient(135deg, #1e40af, #3b82f6);
  color: white;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
}

.tab-count {
  background: rgba(255, 255, 255, 0.9);
  color: #3b82f6;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 12px;
  min-width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tab-btn.active .tab-count {
  background: rgba(255, 255, 255, 0.9);
  color: #3b82f6;
}

.loading-container {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 60px;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.loading-spinner i {
  font-size: 3rem;
  color: #3b82f6;
  margin-bottom: 16px;
}

.loading-container p {
  font-size: 1.1rem;
  color: #64748b;
  margin: 0;
}

.content-area {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.tab-content {
  min-height: 400px;
}

.transfers-list {
  padding: 24px;
}

.transfer-item {
  display: flex;
  align-items: center;
  padding: 20px;
  margin-bottom: 16px;
  border-radius: 16px;
  background: white;
  border-left: 4px solid #3b82f6;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.transfer-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.transfer-item:last-child {
  margin-bottom: 0;
}

.received-transfer {
  border-left-color: #10b981;
}

.transfer-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1e40af, #3b82f6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  margin-right: 20px;
  flex-shrink: 0;
}

.transfer-avatar.received {
  background: linear-gradient(135deg, #10b981, #059669);
}

.transfer-details {
  flex: 1;
}

.transfer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.recipient, .sender, .transaction-label {
  font-weight: 600;
  color: #2d3748;
  font-size: 1rem;
}

.amount {
  font-weight: 700;
  font-size: 1.2rem;
}

.amount.negative {
  color: #e53e3e;
}

.amount.positive {
  color: #10b981;
}

.transfer-meta {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
  font-size: 0.9rem;
  color: #64748b;
}

.reference {
  font-family: 'Courier New', monospace;
  background: #f7fafc;
  padding: 2px 8px;
  border-radius: 6px;
}

.status-badge {
  font-size: 0.8rem;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-success {
  background: #d1fae5;
  color: #065f46;
}

.status-pending {
  background: #fef3c7;
  color: #92400e;
}

.status-error {
  background: #fee2e2;
  color: #991b1b;
}

.status-unknown {
  background: #f1f5f9;
  color: #475569;
}

.empty-state {
  text-align: center;
  padding: 80px 40px;
  color: #64748b;
}

.empty-state i {
  font-size: 4rem;
  color: #cbd5e1;
  margin-bottom: 24px;
}

.empty-state h3 {
  font-size: 1.5rem;
  color: #2d3748;
  margin-bottom: 12px;
}

.empty-state p {
  font-size: 1rem;
  margin: 0;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .transfer-history {
    padding: 16px;
  }

  .header-section {
    padding: 24px;
  }

  .page-title {
    font-size: 1.6rem;
  }

  .date-filters {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .tabs-navigation {
    flex-wrap: wrap;
  }

  .tab-btn {
    min-width: calc(50% - 4px);
  }

  .transfer-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .transfer-avatar {
    margin-right: 0;
  }

  .transfer-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .transfer-meta {
    flex-direction: column;
    gap: 8px;
  }
}
</style>